/**
 * EmailAutomationSendHistory
 *
 * The send history in the email automation contact modal: every attempt on
 * the contact — accepted sends, failed, held and unconfirmed tries — each with
 * SendGrid's own words, as the SMS contact modal shows its sends.
 *
 * The API returns raw rows, ordered by step, then attempt time, then id. A
 * contact that was stuck before failed sends were stopped can carry ~300
 * identical failures on one step, and a contact waiting on a held step is
 * tried once an hour, so `groupSendHistory` collapses a run of the same
 * failure into one line with a count and a date range. Nothing is hidden: the
 * count is the number of rows behind the line.
 *
 * A request-info lead shows on the last line of the step it came from.
 */
import type { AutomationContactSend, ContactLead } from '@/services/marketing.service';
import { formatPacificDateTime } from '@/lib/facilityTimezone';
import { cn } from '@/lib/utils';
import { ContactLeadList, placeLeadsOnSends } from './ContactLeads';

/** One line of the history: a single send, or a run of identical failures. */
export interface SendHistoryEntry {
  /** The first row's sendId — stable across renders. */
  key: string;
  stepId: number | null;
  stepOrder: number;
  subject: string;
  deliveryStatus: string;
  reason: string;
  /** Rows behind this line; above 1 only for a run of identical failures. */
  count: number;
  /** attemptedAt of the first and last row in the line. */
  firstAt: string | null;
  lastAt: string | null;
  opens: number;
  clicks: number;
}

/** Rows that repeat: a run of them with the same reason is one line. */
const GROUPED = new Set(['failed', 'held']);

/**
 * Groups consecutive failed rows — or consecutive held rows — on the same step
 * with the identical reason into one entry. A different status, reason or
 * step, or any other row in between (a sent row, say), starts a new entry.
 * Order is kept as the API sent it.
 */
export function groupSendHistory(sends: AutomationContactSend[]): SendHistoryEntry[] {
  const entries: SendHistoryEntry[] = [];

  for (const s of sends) {
    const status = s.deliveryStatus ?? '';
    const reason = s.reason ?? '';
    const stepId = s.stepId ?? null;
    const stepOrder = Number(s.stepOrder) || 0;
    const at = s.attemptedAt ?? s.sentAt ?? null;
    const prev = entries[entries.length - 1];

    if (
      prev
      && GROUPED.has(status)
      && prev.deliveryStatus === status
      && prev.reason === reason
      && prev.stepOrder === stepOrder
      && prev.stepId === stepId
    ) {
      prev.count += 1;
      prev.lastAt = at ?? prev.lastAt;
      continue;
    }

    entries.push({
      key: String(s.sendId),
      stepId,
      stepOrder,
      subject: s.subject ?? '',
      deliveryStatus: status,
      reason,
      count: 1,
      firstAt: at,
      lastAt: at,
      opens: Number(s.open) || 0,
      clicks: Number(s.clicks) || 0,
    });
  }

  return entries;
}

/** Tries not known to have gone out: no opens or clicks to report. */
function isTry(status: string): boolean {
  return status === 'failed' || status === 'held' || status === 'unconfirmed';
}

/**
 * Statuses whose `reason` explains what went wrong. A send that SendGrid
 * confirmed after a slow answer keeps the timeout text in `reason`, which
 * says nothing about how it was delivered.
 */
const SHOWS_REASON = new Set(['failed', 'held', 'unconfirmed', 'bounce', 'dropped']);

/** Whether a send's `reason` is worth showing for this status (see SHOWS_REASON). */
export function sendShowsReason(status: string): boolean {
  return SHOWS_REASON.has(status);
}

function runLabel(status: string, count: number): string {
  return `${status === 'held' ? 'Held' : 'Failed'} ${count.toLocaleString()} times`;
}

/** A send's status as the history labels it; the contacts list's step column uses it too. */
export function sendStatusLabel(status: string): string {
  if (!status) return 'Unknown';
  return status.charAt(0).toUpperCase() + status.slice(1);
}

/** Badge colours per send status, shared with the contacts list's step column. */
export const SEND_STATUS_BADGE: Record<string, string> = {
  delivered: 'bg-[#d4edda] text-[#155724]',
  processed: 'bg-[#d4edda] text-[#155724]',
  sent: 'bg-[#e7f3ff] text-[#0077a8]',
  deferred: 'bg-[#fff3cd] text-[#856404]',
  pending: 'bg-[#fff3cd] text-[#856404]',
  bounce: 'bg-[#f8d7da] text-[#721c24]',
  dropped: 'bg-[#f8d7da] text-[#721c24]',
  failed: 'bg-[#f8d7da] text-[#721c24]',
  held: 'bg-[#ffe8cc] text-[#8a4b08]',
  unconfirmed: 'bg-[#fff3cd] text-[#856404]',
};

const CARD: Record<string, string> = {
  bounce: 'bg-[#fff5f5]',
  dropped: 'bg-[#fff5f5]',
  failed: 'bg-[#fff5f5]',
  held: 'bg-[#fffaf0]',
  unconfirmed: 'bg-[#fffaf0]',
};

interface EmailAutomationSendHistoryProps {
  sends: AutomationContactSend[];
  /** Request-info leads this contact's emails produced. */
  leads?: ContactLead[];
  /** The viewer's zone; send times are stored as Pacific wall-clock. */
  tz: string;
}

export function EmailAutomationSendHistory({ sends, leads = [], tz }: EmailAutomationSendHistoryProps) {
  const entries = groupSendHistory(sends);
  const placed = placeLeadsOnSends(entries, leads);

  if (entries.length === 0) {
    return (
      <>
        <div className="py-5 text-center text-[13px] text-[#999]">No emails sent to this contact yet.</div>
        <ContactLeadList leads={placed.unplaced} tz={tz} showStep />
      </>
    );
  }

  return (
    <>
      <ol aria-label="Send history" className="m-0 list-none space-y-2.5 p-0">
        {entries.map((e) => {
          const run = e.count > 1;
          const tried = isTry(e.deliveryStatus);
          const entryLeads = placed.byKey.get(e.key) ?? [];
          return (
            <li key={e.key} className={cn('rounded-md px-3.5 py-3', CARD[e.deliveryStatus] ?? 'bg-[#f8f9fa]')}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 text-sm">
                  <strong className="text-[#333]">Step {e.stepOrder}</strong>
                  {e.subject && <span className="text-[#666]"> · {e.subject}</span>}
                </div>
                <span
                  className={cn(
                    'inline-block shrink-0 whitespace-nowrap rounded-xl px-2.5 py-0.5 text-xs font-semibold',
                    SEND_STATUS_BADGE[e.deliveryStatus] ?? 'bg-[#e9ecef] text-[#666]',
                  )}
                >
                  {run ? runLabel(e.deliveryStatus, e.count) : sendStatusLabel(e.deliveryStatus)}
                </span>
              </div>

              {e.deliveryStatus === 'held' && (
                <div className="mt-1.5 text-[13px] leading-normal text-[#8a4b08]">
                  SendGrid refused this step for a reason that is not this contact&apos;s, so {run ? 'these tries' : 'this try'} did
                  not count against them. The step held its sending and tries again an hour later.
                </div>
              )}

              {e.deliveryStatus === 'unconfirmed' && (
                <div className="mt-1.5 text-[13px] leading-normal text-[#856404]">
                  SendGrid did not answer in time, so this email may have gone out. If SendGrid confirms it, this line
                  shows its delivery status and the step is not sent again; if not, it is sent again two hours after this
                  try.
                </div>
              )}

              <div className="mt-1.5 text-xs text-[#888]">
                {run ? (
                  <>First {formatPacificDateTime(e.firstAt, tz)} · Last {formatPacificDateTime(e.lastAt, tz)}</>
                ) : (
                  <>
                    {tried ? 'Tried' : 'Sent'} {formatPacificDateTime(e.firstAt, tz)}
                    {!tried && (
                      <> · {e.opens} open{e.opens === 1 ? '' : 's'} · {e.clicks} click{e.clicks === 1 ? '' : 's'}</>
                    )}
                  </>
                )}
              </div>

              {e.reason && SHOWS_REASON.has(e.deliveryStatus) && (
                <div className="mt-2 rounded-md border border-[#f8d7da] bg-[#fff5f5] px-3 py-2.5">
                  <div className="mb-0.5 text-[11px] font-semibold uppercase text-[#721c24]">
                    {tried ? "SendGrid's message" : 'Reason'}
                  </div>
                  <div className="break-words text-[13px] leading-normal text-[#721c24]">{e.reason}</div>
                </div>
              )}

              <ContactLeadList leads={entryLeads} tz={tz} className="mt-2" />
            </li>
          );
        })}
      </ol>
      <ContactLeadList leads={placed.unplaced} tz={tz} showStep className="mt-2.5" />
    </>
  );
}
